import type { BlobInput } from "@pdfking/blob"
import { Broadcast, ProcessIterable } from "@pdfking/tfunc"

import type { StreamInput } from "#public"

import { StreamProcess } from "./StreamProcess"

/**
 * Whatever a caller has, as many times as it is needed.
 *
 * A stream is read once, and two readers of one are two readers of nothing:
 * the second finds it drained. So it is walked once here — {@link
 * StreamProcess} without answers of its own, so a part comes
 * out as it went in and a blob is still a blob, unread — and every part is
 * handed to all the copies at once.
 *
 * The walk starts when this is called rather than when a copy is iterated, and
 * what a copy has not taken yet is held for it: a caller that reads one copy
 * to the end before starting the next is reading from memory by then, which is
 * what makes writing and serving the same bytes possible at all — and what
 * makes this the wrong tool for a stream too big to hold.
 *
 * @param input - A stream, bytes, something blob-shaped, or a sequence of them
 * @param length - How many copies to hand back
 * @returns That many generators, each yielding every part
 *
 * @example
 * ```ts
 * import { StreamCopy, StreamHash } from "@pdfking/io"
 *
 * // one download, hashed and kept
 * const [hashed, kept] = StreamCopy(response.body, 2)
 * const [hash, blob] = await WaitPromises([StreamHash(hashed), BlobCollect(kept)])
 * ```
 */
export const StreamCopy = (input: StreamInput, length: number): AsyncGenerator<BlobInput>[] => {
  const { push, stop, subscribe } = Broadcast<BlobInput>()
  const copies = Array.from({ length }, subscribe)

  void ProcessIterable(StreamProcess(input), push, stop)

  return copies
}
